/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectorRegistry } from './connector-registry.service';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { MigrationConnection, MigrationConnector } from './migration.models';

/**
 * Add/edit a connection. The form is generated from the connector's field
 * list — secret fields are typed `password`, sent to the migration service
 * for tests, and stripped by MigrationService before anything is persisted.
 * On edit they start blank: the repository copy never held them, and the
 * dialog never echoes one back.
 */
@Component({
  selector: 'app-connection-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatIconModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <h2 mat-dialog-title>{{ (data?.connection ? 'DMS.MIGRATION.CONN_EDIT' : 'DMS.MIGRATION.CONN_ADD') | translate }}</h2>

    <mat-dialog-content class="aca-mig__dlg">
      <!-- connector picker (create) — locked when the wizard opened this
           dialog: the connection must end up matching the job's source
           connector or it could never serve the job -->
      <label class="aca-mig__field" *ngIf="!data?.connection">
        <span class="aca-mig__label">{{ 'DMS.MIGRATION.F_CONNECTOR' | translate }}</span>
        <select class="aca-mig__select" [(ngModel)]="connectorId" [disabled]="!!data?.connectorId">
          <option *ngFor="let c of usableConnectors" [value]="c.id">{{ c.titleKey | translate }}</option>
        </select>
      </label>

      <ng-container *ngIf="connector as conn">
        <!-- connector capability note -->
        <p class="aca-mig__notice aca-mig__notice--info">
          <mat-icon>info_outline</mat-icon>
          <span>{{ conn.descKey | translate }}</span>
        </p>

        <ng-container *ngFor="let field of conn.fields; trackBy: trackField">
          <div class="aca-mig__field">
            <label class="aca-mig__label" [for]="'mig-cf-' + field.key">
              {{ field.labelKey | translate }}
              <span *ngIf="field.required" aria-hidden="true">*</span>
              <span class="aca-mig__hint" *ngIf="field.secret">{{ 'DMS.MIGRATION.SECRET_HINT' | translate }}</span>
            </label>

            <input
              *ngIf="field.type === 'text' || field.type === 'url' || field.type === 'password'"
              [id]="'mig-cf-' + field.key"
              class="aca-mig__input"
              [type]="field.type === 'password' ? 'password' : 'text'"
              [attr.inputmode]="field.type === 'url' ? 'url' : 'text'"
              [(ngModel)]="form[field.key]"
              [placeholder]="field.placeholderKey ? (field.placeholderKey | translate) : ''"
              autocomplete="off"
            />
            <textarea
              *ngIf="field.type === 'textarea'"
              [id]="'mig-cf-' + field.key"
              class="aca-mig__input"
              rows="3"
              [(ngModel)]="form[field.key]"
              [placeholder]="field.placeholderKey ? (field.placeholderKey | translate) : ''"
            ></textarea>
            <input *ngIf="field.type === 'number'" [id]="'mig-cf-' + field.key" class="aca-mig__input" type="number" [(ngModel)]="form[field.key]" />
            <select *ngIf="field.type === 'select'" [id]="'mig-cf-' + field.key" class="aca-mig__select" [(ngModel)]="form[field.key]">
              <option *ngFor="let o of field.options" [value]="o.value">{{ o.labelKey | translate }}</option>
            </select>
            <label *ngIf="field.type === 'checkbox'" style="display: flex; align-items: center; gap: 8px; font-size: 12.5px">
              <input type="checkbox" [(ngModel)]="form[field.key]" />
              {{ 'DMS.MIGRATION.ENABLED' | translate }}
            </label>
            <span class="aca-mig__hint" *ngIf="field.hintKey">{{ field.hintKey | translate }}</span>
          </div>
        </ng-container>

        <!-- test result — only ever the engine's real answer -->
        <p class="aca-mig__notice" *ngIf="testResult() as r" [class.aca-mig__notice--info]="!r.ok">
          <mat-icon>{{ r.ok ? 'check_circle' : 'error_outline' }}</mat-icon>
          <span>
            {{ (r.ok ? 'DMS.MIGRATION.TEST_OK' : 'DMS.MIGRATION.TEST_FAIL') | translate }}
            <ng-container *ngIf="r.server"> — {{ r.server.edition }} {{ r.server.version?.display || r.server.version }} </ng-container>
          </span>
        </p>
      </ng-container>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button type="button" class="aca-mig__btn" mat-dialog-close>{{ 'DMS.MIGRATION.CANCEL' | translate }}</button>
      <button
        type="button"
        class="aca-mig__btn"
        [disabled]="api.available() !== true || busy()"
        [title]="api.available() === true ? '' : ('DMS.MIGRATION.NEEDS_BACKEND' | translate)"
        (click)="test()"
      >
        {{ 'DMS.MIGRATION.TEST_CONNECTION' | translate }}
      </button>
      <button type="button" class="aca-mig__btn aca-mig__btn--primary" [disabled]="!valid() || busy()" (click)="save()">
        {{ 'DMS.MIGRATION.SAVE' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .aca-mig__dlg {
        min-width: 420px;
      }
    `
  ]
})
export class ConnectionDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ConnectionDialogComponent>);
  readonly data: { connection?: MigrationConnection; connectorId?: string } | null = inject(MAT_DIALOG_DATA);
  private readonly service = inject(MigrationService);
  private readonly registry = inject(ConnectorRegistry);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  readonly api = inject(MigrationApiService);

  connectorId = '';
  form: Record<string, any> = {};
  readonly busy = signal(false);
  readonly testResult = signal<{ ok: boolean; server?: any } | null>(null);

  get usableConnectors(): MigrationConnector[] {
    return this.registry.usable();
  }

  get connector(): MigrationConnector | undefined {
    return this.registry.get(this.connectorId);
  }

  ngOnInit(): void {
    const existing = this.data?.connection;
    if (existing) {
      this.connectorId = existing.connectorId;
      // secrets were never persisted, so the form cannot prefill them
      this.form = { ...existing.config };
    } else {
      this.connectorId = this.data?.connectorId ?? this.usableConnectors[0]?.id ?? '';
      const connector = this.connector;
      if (connector) {
        for (const f of connector.fields) {
          if (f.type === 'checkbox') {
            this.form[f.key] = true;
          } else if (f.type === 'select' && f.options?.length) {
            this.form[f.key] = f.options[0].value;
          } else {
            this.form[f.key] = '';
          }
        }
      }
    }
  }

  trackField(_i: number, f: { key: string }): string {
    return f.key;
  }

  valid(): boolean {
    const conn = this.connector;
    if (!conn) {
      return false;
    }
    return conn.fields
      .filter((f) => f.required)
      .every((f) => {
        const v = this.form[f.key];
        // on edit a required secret may be blank = keep the stored one
        if (f.secret && this.data?.connection && (v === '' || v === undefined)) {
          return true;
        }
        return v !== '' && v !== undefined && v !== null;
      });
  }

  test(): void {
    const conn = this.connector;
    if (!conn || !this.api.available()) {
      return;
    }
    this.busy.set(true);
    this.api.testConnection(conn.id, this.form, this.data?.connection?.id).subscribe({
      next: (res) => {
        this.busy.set(false);
        this.testResult.set(res ?? { ok: true });
      },
      error: (e) => {
        this.busy.set(false);
        this.testResult.set({ ok: false });
        // a changed address never borrows the kept password: it has to be typed again
        this.notification.showError(
          e?.error?.code === 'secret-required'
            ? this.translate.instant('DMS.MIGRATION.SECRET_REQUIRED')
            : (e?.error?.detail ?? this.translate.instant('DMS.MIGRATION.TEST_FAIL'))
        );
      }
    });
  }

  save(): void {
    const conn = this.connector;
    if (!conn || !this.valid()) {
      return;
    }
    const existing = this.data?.connection;
    const record: MigrationConnection = {
      id: existing?.id ?? `conn-${Date.now().toString(36)}`,
      name: String(this.form['name'] ?? conn.id),
      connectorId: conn.id,
      enabled: existing?.enabled ?? true,
      config: this.form,
      createdBy: existing?.createdBy ?? 'admin',
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      lastTest: this.testResult()?.ok ? { ok: true, at: new Date().toISOString() } : existing?.lastTest
    };
    this.busy.set(true);
    this.service.saveConnection(record).subscribe({
      next: () => {
        this.busy.set(false);
        this.registerSecretsWithEngine(record);
        this.dialogRef.close(record);
      },
      error: () => {
        this.busy.set(false);
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }

  /**
   * Hands the full form — secrets included — to the engine's vault so runs
   * can authenticate later. Only fires when the form actually carried secret
   * values; the repository copy never sees them. A failure is a warning, not
   * a save failure: the connection exists, its credentials just need a
   * successful Test once.
   */
  private registerSecretsWithEngine(record: MigrationConnection): void {
    if (this.api.available() !== true) {
      return;
    }
    const secretKeys = (this.connector?.fields ?? []).filter((f) => f.secret).map((f) => f.key);
    const hasSecretValues = secretKeys.some((k) => this.form[k] !== '' && this.form[k] !== undefined && this.form[k] !== null);
    if (!hasSecretValues) {
      return;
    }
    this.api.registerSecrets(record.id, record.connectorId, this.form).subscribe({
      error: () => this.notification.showError(this.translate.instant('DMS.MIGRATION.CONN_REGISTER_FAIL'))
    });
  }
}
